import React from 'react';
import { DossierClient, SousDossier } from '../utils/types';
import { formatDate } from '../utils/helpers';
import { canDeleteSubfoldersAndFiles } from '../utils/permissions';

interface DossierModalProps {
  isOpen: boolean;
  dossier: DossierClient;
  currentUserRole: string;
  onClose: () => void;
  onOpenSousDossier: (sousDossier: SousDossier) => void;
  onDeleteSousDossier: (sousDossier: SousDossier) => void;
  onDeleteDossier: (dossier: DossierClient) => void;
}

const DossierModal: React.FC<DossierModalProps> = ({
  isOpen,
  dossier,
  currentUserRole,
  onClose,
  onOpenSousDossier,
  onDeleteSousDossier,
  onDeleteDossier
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl sm:rounded-3xl max-w-full lg:max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl border-2 sm:border-4 border-blue-200 mx-2 sm:mx-4">
        <ModalHeader dossier={dossier} onClose={onClose} />
        <ModalContent
          dossier={dossier}
          currentUserRole={currentUserRole}
          onOpenSousDossier={onOpenSousDossier}
          onDeleteSousDossier={onDeleteSousDossier}
          onDeleteDossier={onDeleteDossier}
          onClose={onClose}
        />
      </div>
    </div>
  );
};

const ModalHeader: React.FC<{ dossier: DossierClient; onClose: () => void }> = ({ dossier, onClose }) => (
  <div className="flex items-center justify-between p-4 sm:p-6 border-b-2 sm:border-b-4 border-blue-300 bg-gradient-to-r from-blue-600 to-indigo-600">
    <div className="flex-1 min-w-0">
      <h3 className="text-lg sm:text-xl font-semibold text-white truncate">
        {dossier.typeDocument === 'voyage' ? 'Dossier Voyage' : 'Dossier'} : {dossier.nom} {dossier.prenom}
      </h3>
      <p className="text-blue-100 text-xs sm:text-sm mt-1 truncate">
        {dossier.email} | {dossier.telephone} | Référence: {dossier.reference}
      </p>
    </div>
    <button
      onClick={onClose}
      className="text-white hover:text-gray-200 p-2 hover:bg-white/20 rounded-full transition-all duration-300 flex-shrink-0 ml-2"
    >
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

const ModalContent: React.FC<{
  dossier: DossierClient;
  currentUserRole: string;
  onOpenSousDossier: (sousDossier: SousDossier) => void;
  onDeleteSousDossier: (sousDossier: SousDossier) => void;
  onDeleteDossier: (dossier: DossierClient) => void;
  onClose: () => void;
}> = ({ dossier, currentUserRole, onOpenSousDossier, onDeleteSousDossier, onDeleteDossier, onClose }) => (
  <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
    <InfoSections dossier={dossier} />
    <SousDossiersSection
      dossier={dossier}
      currentUserRole={currentUserRole}
      onOpenSousDossier={onOpenSousDossier}
      onDeleteSousDossier={onDeleteSousDossier}
    />
    <ActionsSection
      dossier={dossier}
      currentUserRole={currentUserRole}
      onClose={onClose}
      onDeleteDossier={onDeleteDossier}
    />
  </div>
);

const InfoSections: React.FC<{ dossier: DossierClient }> = ({ dossier }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
    <InfoSection
      title="Informations client"
      icon="user"
      color="blue"
      content={
        <>
          <InfoItem label="Nom complet" value={`${dossier.nom} ${dossier.prenom}`} />
          <InfoItem label="Email" value={dossier.email} />
          <InfoItem label="Téléphone" value={dossier.telephone || 'Non renseigné'} />
          <InfoItem label="Profession" value={dossier.profession} />
          <InfoItem label="Sexe" value={dossier.sexe === 'H' ? 'Homme' : 'Femme'} />
        </>
      }
    />
    <InfoSection
      title="Informations générales"
      icon="document"
      color="indigo"
      content={
        <>
          <InfoItem label="Type" value={dossier.typeDocument === 'voyage' ? 'Voyage' : 'Transfert'} />
          <InfoItem label="Référence" value={dossier.reference} />
          <InfoItem 
            label="Statut" 
            value={
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                dossier.statut === 'en_attente' ? 'bg-yellow-100 text-yellow-800' :
                dossier.statut === 'partiellement_apuré' ? 'bg-orange-100 text-orange-800' :
                dossier.statut === 'apuré' ? 'bg-green-100 text-green-800' :
                dossier.statut === 'archivé' ? 'bg-blue-100 text-blue-800' :
                'bg-red-100 text-red-800'
              }`}>
                {dossier.statut}
              </span>
            } 
          />
          <InfoItem label="Date création" value={formatDate(dossier.dateCreation)} />
          <InfoItem label="Sous-dossiers" value={dossier.sousDossiers.length.toString()} />
        </>
      }
    />
  </div>
);

const InfoSection: React.FC<{
  title: string;
  icon: string;
  color: 'blue' | 'indigo';
  content: React.ReactNode;
}> = ({ title, icon, color, content }) => {
  const colorClasses = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-500' },
    indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-500' }
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'user':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />;
      case 'document':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />;
      default:
        return null;
    }
  };

  return (
    <div className={`${colorClasses[color].bg} p-3 sm:p-4 rounded-lg`}>
      <h4 className="font-medium text-gray-900 mb-2 sm:mb-3 flex items-center">
        <svg className={`w-4 h-4 sm:w-5 sm:h-5 mr-2 ${colorClasses[color].icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {getIcon(icon)}
        </svg>
        {title}
      </h4>
      <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
        {content}
      </div>
    </div>
  );
};

const InfoItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <span className="font-medium text-gray-500">{label}:</span>
    <div className="text-gray-900">{value}</div>
  </div>
);

const SousDossiersSection: React.FC<{
  dossier: DossierClient;
  currentUserRole: string;
  onOpenSousDossier: (sousDossier: SousDossier) => void;
  onDeleteSousDossier: (sousDossier: SousDossier) => void;
}> = ({ dossier, currentUserRole, onOpenSousDossier, onDeleteSousDossier }) => (
  <div className="bg-white border border-blue-200 rounded-xl p-4 sm:p-6">
    <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">
      Sous-dossiers ({dossier.sousDossiers.length})
    </h4>

    <div className="h-full max-h-full overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-3 sm:p-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {dossier.sousDossiers.map((sousDossier, index) => (
          <SousDossierCard
            key={index}
            sousDossier={sousDossier}
            dossierType={dossier.typeDocument}
            currentUserRole={currentUserRole}
            onOpenSousDossier={onOpenSousDossier}
            onDeleteSousDossier={onDeleteSousDossier}
          />
        ))}
      </div>

      {dossier.sousDossiers.length === 0 && (
        <div className="text-center py-6 sm:py-8 text-gray-500">
          <svg className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
          </svg>
          <p className="mt-2 text-sm">Aucun sous-dossier dans ce dossier</p>
        </div>
      )}
    </div>
  </div>
);

const SousDossierCard: React.FC<{
  sousDossier: SousDossier;
  dossierType: string;
  currentUserRole: string;
  onOpenSousDossier: (sousDossier: SousDossier) => void;
  onDeleteSousDossier: (sousDossier: SousDossier) => void;
}> = ({ sousDossier, dossierType, currentUserRole, onOpenSousDossier, onDeleteSousDossier }) => (
  <div className="border border-gray-200 rounded-xl p-3 sm:p-4 hover:shadow-md hover:border-blue-300 transition-all duration-300 bg-white">
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between mb-2 sm:mb-3">
        <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h5 className="font-medium text-gray-900 truncate text-sm sm:text-base" title={sousDossier.nom}>
              {sousDossier.nom}
            </h5>
            <p className="text-xs text-gray-600">{sousDossier.fichiers.length} fichier(s)</p>
            <p className="text-xs text-gray-500">Créé le {formatDate(sousDossier.date)}</p>
            
            {dossierType === 'voyage' ? (
              <div className="mt-1 sm:mt-2 text-xs text-gray-600">                 
                <p className="truncate"><strong>Pays:</strong> {Array.isArray(sousDossier.pays) ? sousDossier.pays?.join(', ') : sousDossier.pays}</p>
                <p className="truncate"><strong>Raison:</strong> {sousDossier.raison === 'autres' ? sousDossier.autreRaison : sousDossier.raison}</p>
              </div>
            ) : (
              <div className="mt-1 sm:mt-2 text-xs text-gray-600">
                <p className="truncate"><strong>Type:</strong> {sousDossier.typeTransfert}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-auto pt-2 sm:pt-3 border-t border-gray-100">
        <button
          onClick={() => onOpenSousDossier(sousDossier)}
          className="text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg transition-all duration-300 text-xs sm:text-sm font-medium flex items-center"
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Voir détails
        </button>
        
        {canDeleteSubfoldersAndFiles(currentUserRole) && (
          <button
            onClick={() => onDeleteSousDossier(sousDossier)}
            className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg transition-all duration-300 text-xs sm:text-sm font-medium flex items-center"
          >
            <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer
          </button>
        )}
      </div>
    </div>
  </div>
);

const ActionsSection: React.FC<{
  dossier: DossierClient;
  currentUserRole: string;
  onClose: () => void;
  onDeleteDossier: (dossier: DossierClient) => void;
}> = ({ dossier, currentUserRole, onClose, onDeleteDossier }) => (
  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t flex justify-end space-x-2 sm:space-x-3">
    <button
      onClick={onClose}
      className="px-3 sm:px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-300 text-sm"
    >
      Fermer
    </button>
    {(currentUserRole === 'admin_bank' || currentUserRole === 'superviseur') && dossier.statut !== 'rejeté' && (
      <button
        onClick={() => onDeleteDossier(dossier)}
        className="px-3 sm:px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all duration-300 text-sm"
      >
        Supprimer le dossier
      </button>
    )}
  </div>
);

export default DossierModal;
